import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaCog, FaSpinner } from 'react-icons/fa';
import ChatSettingsCard from '../../components/admin/ChatSettingsCard';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}`;

const Settings = () => {
    // State for the toggle, loading, and saving status
    const [isBonusModeOn, setIsBonusModeOn] = useState(false);
    const [enableLessonHyperlinks, setEnableLessonHyperlinks] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingHyperlinks, setIsSavingHyperlinks] = useState(false);
    const [error, setError] = useState('');

    // Fetch the current settings from the backend when the component loads
    const fetchSettings = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const [bonusResponse, hyperlinksResponse] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/v1/admin/bonus-setting`),
                axios.post(`${API_BASE_URL}/api/v1/admin/get-settings`)
            ]);
            // Assuming the backend returns { isBonusModeActive: true/false }
            setIsBonusModeOn(bonusResponse.data?.data?.isBonusModeActive);
            // Set hyperlinks setting, handle null/undefined properly
            const hyperlinksValue = hyperlinksResponse.data?.data?.allowLessonHyperlinks;
            if (hyperlinksValue === false || hyperlinksValue === "false" || hyperlinksValue === 0) {
                setEnableLessonHyperlinks(false);
            } else if (hyperlinksValue === true || hyperlinksValue === "true" || hyperlinksValue === 1) {
                setEnableLessonHyperlinks(true);
            } else {
                // Default to true if null/undefined (backward compatibility)
                setEnableLessonHyperlinks(true);
            }
        } catch (err) {
            console.error("Failed to fetch settings:", err);
            setError(err.response?.data?.message || 'Could not load settings. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // Handle the toggle change
    const handleToggleChange = async () => {
        if (isSaving || isLoading) return; // Prevent multiple clicks while saving/loading
        const newSettingValue = !isBonusModeOn;
        setIsSaving(true);
        setIsBonusModeOn(newSettingValue); // Optimistically update the UI
        try {
            // Send the new setting to the backend
            await axios.put(`${API_BASE_URL}/api/v1/admin/update-bonus-setting`, {
                isProvideBonusLesson: newSettingValue
            });
            toast.success(`Bonus Lessons are now ${newSettingValue ? 'ON' : 'OFF'}`);
        } catch (err) {
            console.error("Failed to update settings:", err);
            toast.error('Failed to save setting. Reverting change.');
            setIsBonusModeOn(!newSettingValue); // Revert UI on failure
            setError(err.response?.data?.message || 'Could not save setting.');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle the hyperlinks toggle change
    const handleHyperlinksToggleChange = async () => {
        if (isSavingHyperlinks || isLoading) return;

        const newSettingValue = !enableLessonHyperlinks;
        setIsSavingHyperlinks(true);
        setEnableLessonHyperlinks(newSettingValue); // Optimistically update the UI

        try {
            // Send the new setting to the backend - explicitly convert to string to ensure it's sent correctly
            const response = await axios.post(`${API_BASE_URL}/api/v1/admin/update-settings`, null, {
                params: { key: 'allowLessonHyperlinks', value: String(newSettingValue) }
            });
            console.log("✅ Hyperlinks setting saved:", response.data);
            // Verify the setting was saved by fetching it again
            const verifyResponse = await axios.post(`${API_BASE_URL}/api/v1/admin/get-settings`);
            console.log("✅ Verified setting:", verifyResponse.data);
            toast.success(`Lesson hyperlinks are now ${newSettingValue ? 'ON' : 'OFF'}`);
        } catch (err) {
            console.error("❌ Failed to update hyperlinks setting:", err);
            toast.error('Failed to save setting. Reverting change.');
            setEnableLessonHyperlinks(!newSettingValue); // Revert UI on failure
            setError(err.response?.data?.message || 'Could not save setting.');
        } finally {
            setIsSavingHyperlinks(false);
        }
    };

    return (
        <div className="min-h-full">
            {/* Header Section */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            System Settings
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Configure your SmartStart AI platform settings
                        </p>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-12 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full mb-4">
                        <FaSpinner className="animate-spin h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Loading Settings</h3>
                    <p className="text-gray-600 dark:text-gray-400">Please wait while we fetch your settings...</p>
                </div>
            ) : error ? (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                        </div>
                    </div>
                </div>
            ) : (
                // Settings Card
                <>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                    <FaCog className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Bonus Lessons Configuration
                                </h2>
                            </div>
                        </div>
                        
                        <div className="p-6">
                            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                                {/* Left Side: Title and Description */}
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        Include Bonus Lessons
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                        Control whether SmartStart AI includes the "Bonus Lessons" in its recommendations. 
                                        When enabled, users will see additional bonus lesson options in their personalized plans.
                                    </p>
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                            isBonusModeOn 
                                                ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                        }`}>
                                            {isBonusModeOn ? 'Currently Active' : 'Currently Inactive'}
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Right Side: Toggle Switch */}
                                <div className="flex items-center space-x-4">
                                    <span className={`font-bold text-lg transition-colors duration-200 ${
                                        isBonusModeOn ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                                    }`}>
                                        {isBonusModeOn ? 'ON' : 'OFF'}
                                    </span>
                                    <button
                                        onClick={handleToggleChange}
                                        disabled={isSaving}
                                        className={`relative inline-flex items-center h-8 w-16 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                                            isBonusModeOn ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                                        }`}
                                    >
                                        <span
                                            className={`inline-block w-6 h-6 transform bg-white rounded-full transition-transform duration-200 ${
                                                isBonusModeOn ? 'translate-x-9' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                </div>
                            </div>
                            
                            {isSaving && (
                                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                    <div className="flex items-center">
                                        <FaSpinner className="animate-spin h-4 w-4 text-blue-600 mr-2" />
                                        <span className="text-sm text-blue-800 dark:text-blue-200">Saving settings...</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Lesson Hyperlinks Configuration */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden mt-6">
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                                    <FaCog className="w-5 h-5 text-green-600 dark:text-green-400" />
                                </div>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Lesson Hyperlinks Configuration
                                </h2>
                            </div>
                        </div>
                        
                        <div className="p-6">
                            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                                {/* Left Side: Title and Description */}
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        Enable Lesson Hyperlinks
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                        Control whether lesson recommendations are displayed as clickable hyperlinks or plain text. 
                                        When disabled, users will only see the lesson titles without clickable links.
                                    </p>
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                            enableLessonHyperlinks 
                                                ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                        }`}>
                                            {enableLessonHyperlinks ? 'Currently Active' : 'Currently Inactive'}
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Right Side: Toggle Switch */}
                                <div className="flex items-center space-x-4">
                                    <span className={`font-bold text-lg transition-colors duration-200 ${
                                        enableLessonHyperlinks ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                                    }`}>
                                        {enableLessonHyperlinks ? 'ON' : 'OFF'}
                                    </span>
                                    <button
                                        onClick={handleHyperlinksToggleChange}
                                        disabled={isSavingHyperlinks}
                                        className={`relative inline-flex items-center h-8 w-16 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                                            enableLessonHyperlinks ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                                        }`}
                                    >
                                        <span
                                            className={`inline-block w-6 h-6 transform bg-white rounded-full transition-transform duration-200 ${
                                                enableLessonHyperlinks ? 'translate-x-9' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                </div>
                            </div>
                            
                            {isSavingHyperlinks && (
                                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                    <div className="flex items-center">
                                        <FaSpinner className="animate-spin h-4 w-4 text-green-600 mr-2" />
                                        <span className="text-sm text-green-800 dark:text-green-200">Saving settings...</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <ChatSettingsCard />
                </>
            )}
        </div>
    );
};

export default Settings;